import { useState, useCallback } from 'react';
import React from 'react';

export interface PanelConfig {
  id: string;
  title: string;
  row: number;
  colSpan?: number;
}

export const usePanelManager = (initialPanels: PanelConfig[]) => {
  // Track panel order for drag & drop
  const [panelOrder, setPanelOrder] = useState<string[]>(initialPanels.map(p => p.id));

  // Track minimized panels
  const [minimizedPanels, setMinimizedPanels] = useState<Set<string>>(new Set());

  // Drag state
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  // Handle minimize toggle
  const handleMinimize = useCallback((panelId: string) => {
    setMinimizedPanels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(panelId)) {
        newSet.delete(panelId);
      } else {
        newSet.add(panelId);
      }
      return newSet;
    });
  }, []);

  // Handle restore
  const handleRestore = useCallback((panelId: string) => {
    setMinimizedPanels(prev => {
      const newSet = new Set(prev);
      newSet.delete(panelId);
      return newSet;
    });
  }, []);

  // Drag handlers
  const handleDragStart = useCallback((e: React.DragEvent, id: string) => {
    e.dataTransfer.setData('text/plain', id);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedId(id);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, id: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (id !== draggedId) {
      setDragOverId(id);
    }
  }, [draggedId]);

  const handleDrop = useCallback((e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    const sourceId = e.dataTransfer.getData('text/plain');

    if (sourceId && sourceId !== targetId) {
      // Check if panels are in the same row
      const sourcePanel = initialPanels.find(p => p.id === sourceId);
      const targetPanel = initialPanels.find(p => p.id === targetId);

      if (sourcePanel && targetPanel && sourcePanel.row === targetPanel.row) {
        setPanelOrder(prev => {
          const newOrder = [...prev];
          const sourceIdx = newOrder.indexOf(sourceId);
          const targetIdx = newOrder.indexOf(targetId);

          if (sourceIdx !== -1 && targetIdx !== -1) {
            newOrder.splice(sourceIdx, 1);
            newOrder.splice(targetIdx, 0, sourceId);
          }
          return newOrder;
        });
      }
    }

    setDraggedId(null);
    setDragOverId(null);
  }, [initialPanels]);

  const handleDragEnd = useCallback(() => {
    setDraggedId(null);
    setDragOverId(null);
  }, []);

  // Get ordered panels by row
  const getOrderedPanels = useCallback((row: number) => {
    return panelOrder
      .map(id => initialPanels.find(p => p.id === id))
      .filter((p): p is PanelConfig => p !== undefined && p.row === row);
  }, [panelOrder, initialPanels]);

  // Get minimized panel info
  const minimizedPanelsList = Array.from(minimizedPanels).map(id =>
    initialPanels.find(p => p.id === id)
  ).filter((p): p is PanelConfig => p !== undefined);

  // Check if panel is minimized
  const isPanelMinimized = useCallback((panelId: string) => {
    return minimizedPanels.has(panelId);
  }, [minimizedPanels]);

  // Get drag state for a panel
  const getDragState = useCallback((panelId: string) => ({
    isDragging: draggedId === panelId,
    isDragOver: dragOverId === panelId && draggedId !== panelId,
  }), [draggedId, dragOverId]);

  // Get drag props for a panel wrapper
  const getDragProps = useCallback((panelId: string) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent) => handleDragStart(e, panelId),
    onDragOver: (e: React.DragEvent) => handleDragOver(e, panelId),
    onDrop: (e: React.DragEvent) => handleDrop(e, panelId),
    onDragEnd: handleDragEnd,
  }), [handleDragStart, handleDragOver, handleDrop, handleDragEnd]);

  return {
    panelOrder,
    minimizedPanels,
    minimizedPanelsList,
    handleMinimize,
    handleRestore,
    getOrderedPanels,
    isPanelMinimized,
    getDragState,
    getDragProps,
  };
};

export default usePanelManager;
